/**
 * What was captured when the car went out: licence images, the customer's
 * signature and supporting documents. Licence images are fetched on demand —
 * the backend only serves them to a dealer principal or sales manager, and
 * logs every view.
 */
import { useEffect, useState } from 'react';
import { testDrivesService } from '../../lib/services';
import { apiErrorMessage } from '../../lib/apiError';
import { formatInOrgTz } from '../../lib/datetime';

const KIND_LABEL = {
  LICENCE_FRONT: 'Licence — front',
  LICENCE_BACK: 'Licence — back',
  SIGNATURE: 'Signature',
  DOCUMENT: 'Document',
};

function CaptureItem({ c, autoLoad }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const fetchBlob = () => testDrivesService.captureBlob(c.id)
    .then((blob) => setUrl(URL.createObjectURL(blob)))
    .catch((e) => {
      // A blob response hides the JSON error body; the status is enough here.
      const status = e?.response?.status;
      setErr(status === 403 || status === 400
        ? 'Restricted — dealer principal or sales manager only'
        : apiErrorMessage(e, 'Could not load file'));
    })
    .finally(() => setLoading(false));

  const load = () => { setLoading(true); setErr(''); fetchBlob(); };

  useEffect(() => {
    if (autoLoad) fetchBlob();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, c.id]);

  const isImage = (c.mimeType || '').startsWith('image/') && !/hei[cf]/.test(c.mimeType);
  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 600, overflowWrap: 'anywhere' }}>
          {c.kind === 'DOCUMENT' ? (c.label || c.originalFileName || 'Document') : KIND_LABEL[c.kind]}
        </span>
        <span className="mono text-xs text-muted">{formatInOrgTz(c.createdAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
      </div>
      {url && isImage && (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={KIND_LABEL[c.kind]}
               style={{ width: '100%', maxHeight: c.kind === 'SIGNATURE' ? 90 : 180, objectFit: 'contain', background: '#fff', borderRadius: 6 }} />
        </a>
      )}
      {url && !isImage && (
        <a className="btn small" href={url} target="_blank" rel="noreferrer" download={c.originalFileName || undefined}>Open file</a>
      )}
      {!url && !err && (
        <button type="button" className="btn small" disabled={loading} onClick={load}>
          {loading ? 'Loading…' : c.kind.startsWith('LICENCE') ? '🔒 View licence' : 'View'}
        </button>
      )}
      {err && <div className="text-xs text-muted">{err}</div>}
      {c.kind === 'SIGNATURE' && c.signerName && <div className="text-xs">Signed by <b>{c.signerName}</b></div>}
      {c.uploadedByLabel && <div className="mono text-xs text-muted">Captured by {c.uploadedByLabel}</div>}
    </div>
  );
}

/** Keyed by drive so switching drives starts from a clean slate. */
export default function CheckInRecord({ drive }) {
  return <CheckInRecordInner key={`${drive.id}:${drive.updatedAt || ''}`} drive={drive} />;
}

function CheckInRecordInner({ drive }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    testDrivesService.driveCaptures(drive.id)
      .then((d) => { if (alive) setItems(Array.isArray(d) ? d : []); })
      .catch((e) => { if (alive) { setItems([]); setError(apiErrorMessage(e, 'Could not load the check-in record')); } });
    return () => { alive = false; };
  }, [drive.id]);

  const attested = drive.agreementAttestedAt && !drive.signatureDocumentId;

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>Check-in record</div>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 4, fontSize: 13 }}>
        <span className="text-muted">Licence no.</span><span className="mono">{drive.licenceNumberMasked || '—'}</span>
        <span className="text-muted">Licence expiry</span><span>{drive.licenceExpiry || '—'}</span>
        <span className="text-muted">Agreement</span>
        <span>
          {drive.signatureDocumentId
            ? `Signed on screen${drive.signerName ? ` by ${drive.signerName}` : ''}`
            : attested
              ? `Paper form — attested by ${drive.agreementAttestedLabel || 'consultant'}`
              : drive.agreementStatus || 'DRAFT'}
          {(drive.signedAt || drive.agreementAttestedAt) && (
            <span className="mono text-xs text-muted">
              {' · '}{formatInOrgTz(drive.signedAt || drive.agreementAttestedAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </span>
      </div>
      {items === null && <div className="mono text-xs text-muted">Loading…</div>}
      {error && <div className="text-xs text-muted">{error}</div>}
      {items && items.length === 0 && !error && (
        <div className="mono text-xs text-muted">No licence, signature or documents were captured for this drive.</div>
      )}
      {items && items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {items.map((c) => (
            <CaptureItem key={c.id} c={c} autoLoad={c.kind === 'SIGNATURE'} />
          ))}
        </div>
      )}
    </div>
  );
}
